import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CheckCircle2,
  Clock,
  XCircle,
  Loader2,
  Wallet,
  ArrowLeft,
  Home,
  RefreshCw,
  PartyPopper,
  ShieldCheck,
  Coins,
  Hash,
  Landmark,
  Copy,
  Check,
  X,
} from "lucide-react";

import { api, formatApiErrorDetail } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { ProfileShell } from "../../components/profile/ProfileShell";

const POLL_INTERVAL_MS = 3000;
const POLL_MAX_ATTEMPTS = 40; // ~2 minutes

const STATUS_META = {
  pending: {
    icon: Clock,
    title: "Awaiting payment confirmation",
    body:
      "Your deposit is being processed by the payment gateway. Funds will be credited automatically once confirmed — usually within 1–3 minutes.",
    color: "#f5c451",
    softColor: "#fde68a",
    glow: "rgba(245,196,81,0.45)",
    bg:
      "linear-gradient(135deg,rgba(245,196,81,0.12) 0%,rgba(245,196,81,0.04) 100%)",
    border: "rgba(245,196,81,0.35)",
    ribbon: "#f5c451",
  },
  completed: {
    icon: CheckCircle2,
    title: "Deposit successful",
    body:
      "Funds have been credited to your wallet. Time to roll — place a bet, spin the reels, or hit the live tables!",
    color: "#21c46e",
    softColor: "#bbf7d0",
    glow: "rgba(33,196,110,0.55)",
    bg:
      "linear-gradient(135deg,rgba(33,196,110,0.14) 0%,rgba(33,196,110,0.03) 100%)",
    border: "rgba(33,196,110,0.4)",
    ribbon: "#21c46e",
  },
  failed: {
    icon: XCircle,
    title: "Deposit failed",
    body:
      "The gateway reported the payment was not completed. No funds were charged. Please try again with a different method.",
    color: "#ef4444",
    softColor: "#fecaca",
    glow: "rgba(239,68,68,0.4)",
    bg:
      "linear-gradient(135deg,rgba(239,68,68,0.12) 0%,rgba(239,68,68,0.03) 100%)",
    border: "rgba(239,68,68,0.4)",
    ribbon: "#ef4444",
  },
  rejected: {
    icon: XCircle,
    title: "Deposit rejected",
    body:
      "The deposit was rejected by the payment provider. If you were charged, please contact support with your reference below.",
    color: "#ef4444",
    softColor: "#fecaca",
    glow: "rgba(239,68,68,0.4)",
    bg:
      "linear-gradient(135deg,rgba(239,68,68,0.12) 0%,rgba(239,68,68,0.03) 100%)",
    border: "rgba(239,68,68,0.4)",
    ribbon: "#ef4444",
  },
};

function fmtBDT(n) {
  return Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

// Resolve the user-friendly payment-method name (e.g. "bKash (E-wallet)",
// "Nagad (E-wallet)", "USDT (Crypto)") from a deposit doc. Prefers the actual
// method id over the gateway-provider label (which only says "CowPay BD" etc.).
const EWALLET_NAMES = {
  bkash: "bKash",
  nagad: "Nagad",
  rocket: "Rocket",
  upay: "UPay",
  tap: "Tap",
  okwallet: "OK Wallet",
};
const CRYPTO_NAMES = { usdt: "USDT", btc: "BTC", eth: "ETH", trx: "TRX" };
const BANK_NAMES = { bank: "Bank Transfer", bank_transfer: "Bank Transfer" };

// Pretty gateway/provider names (the short brand). `deposit.provider` is the
// canonical id (lowercase, e.g. "cowpay") — map to display form.
const PROVIDER_NAMES = {
  cowpay: "CowPay",
  starpago: "StarPago",
  wpay: "WPay",
  hrpay: "HRpay",
  nowpayments: "NowPayments",
  okpay: "OKPay",
  paypal: "PayPal",
  stripe: "Stripe",
  razorpay: "Razorpay",
  manual: "Manual",
  agent: "Agent",
};

function providerDisplay(deposit) {
  if (!deposit) return "";
  const id = String(deposit.provider || "").toLowerCase();
  if (PROVIDER_NAMES[id]) return PROVIDER_NAMES[id];
  if (id) return id.charAt(0).toUpperCase() + id.slice(1);
  return String(deposit.gateway_label || "");
}

function methodDisplay(deposit) {
  if (!deposit) return "—";
  const id = String(deposit.gateway_method || "").toLowerCase();
  if (EWALLET_NAMES[id]) return `${EWALLET_NAMES[id]} (E-wallet)`;
  if (CRYPTO_NAMES[id]) return `${CRYPTO_NAMES[id]} (Crypto)`;
  if (BANK_NAMES[id]) return BANK_NAMES[id];
  // Fall back to the human "method" string the user picked at submit time,
  // taking the FIRST segment before "·" (e.g. "Nagad · Nagad Payment · CowPay BD" → "Nagad")
  const raw = String(deposit.method || "").split("·")[0].trim();
  if (raw) {
    // Append (E-wallet) when we can detect it from gateway_method category
    const lc = raw.toLowerCase();
    if (EWALLET_NAMES[lc]) return `${EWALLET_NAMES[lc]} (E-wallet)`;
    return raw;
  }
  return (deposit.gateway_label || deposit.provider || "—").toString();
}

function friendlyTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString([], {
      day: "numeric",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function DepositReturnPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const ref = params.get("ref");
  const checkoutUrl = params.get("checkout") || "";
  const [deposit, setDeposit] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [attempts, setAttempts] = useState(0);
  const [celebrated, setCelebrated] = useState(false);
  const timerRef = useRef(null);

  const fetchOnce = async () => {
    if (!ref) return;
    try {
      const { data } = await api.get(`/profile/deposits/${ref}`);
      setDeposit(data);
      setError(null);
      if (data?.status === "completed") {
        await refreshUser();
      }
      return data;
    } catch (err) {
      setError(
        formatApiErrorDetail(err.response?.data?.detail) ||
          "Could not load deposit status"
      );
      return null;
    } finally {
      setLoading(false);
    }
  };

  const requeryGateway = async () => {
    if (!ref) return null;
    try {
      const { data } = await api.post(`/payments/cowpay/requery/${ref}`);
      if (data?.status === "completed") {
        await refreshUser();
        await fetchOnce();
      }
      return data;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    if (!ref) {
      setLoading(false);
      setError("No deposit reference in URL");
      return;
    }
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      const d = await fetchOnce();
      const status = d?.status;
      if (status && status !== "pending") return;
      setAttempts((n) => {
        const next = n + 1;
        if (next === 3 && d?.provider === "cowpay") {
          requeryGateway();
        }
        if (next >= POLL_MAX_ATTEMPTS) return next;
        timerRef.current = setTimeout(tick, POLL_INTERVAL_MS);
        return next;
      });
    };
    tick();
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref]);

  const status = deposit?.status || "pending";
  const meta = STATUS_META[status] || STATUS_META.pending;
  const Icon = meta.icon;
  const isDone = status === "completed";

  // Order number — same value the admin panel's "All Deposits" Gateway
  // Order UID column shows (see baji_deposit_to_admin in server.py). Prefer
  // our internal deposit id (= ShopOrderId we sent to the gateway), then
  // fall back to any provider-supplied tracking ref.
  const gatewayOrderNo =
    deposit?.id ||
    deposit?.gateway_order_no ||
    deposit?.reference ||
    deposit?.provider_order_id ||
    deposit?.provider_txn_id ||
    "";

  const [orderCopied, setOrderCopied] = useState(false);
  const copyOrderNo = async () => {
    try {
      await navigator.clipboard.writeText(String(gatewayOrderNo || ""));
      setOrderCopied(true);
      setTimeout(() => setOrderCopied(false), 1400);
    } catch { /* ignore */ }
  };

  // Fire confetti once when a deposit completes
  useEffect(() => {
    if (isDone && !celebrated) setCelebrated(true);
  }, [isDone, celebrated]);

  // ====== PENDING — render OUTSIDE ProfileShell as a full-screen modal page (bajilo-style) ======
  if (deposit && status === "pending") {
    return (
      <div
        className="min-h-screen flex flex-col"
        style={{ background: "#0a0e15" }}
        data-testid="deposit-return-pending-screen"
      >
        <div className="mx-auto w-full max-w-[440px] flex-1 flex flex-col px-5 pt-3 pb-5">
          {/* Top-right X */}
          <div className="flex justify-end -mr-1">
            <button
              onClick={() => navigate("/profile/wallet")}
              className="h-11 w-11 rounded-full inline-flex items-center justify-center text-white/60 hover:text-white hover:bg-white/[0.05] transition"
              data-testid="deposit-pending-close"
              aria-label="Close"
            >
              <X size={24} strokeWidth={2} />
            </button>
          </div>

          {/* Centered hero */}
          <div className="flex-1 flex flex-col items-center text-center pt-4">
            {/* Hand-drawn hourglass — closed bowtie outline that spins */}
            <div className="mb-7" style={{ animation: "ml_hg_spin 3.2s linear infinite" }}>
              <svg
                width="120"
                height="140"
                viewBox="0 0 120 140"
                fill="none"
                aria-hidden="true"
                style={{ filter: "url(#ml_hg_rough) drop-shadow(0 6px 18px rgba(229,182,73,0.25))" }}
              >
                <defs>
                  {/* Hand-drawn / rough marker texture */}
                  <filter id="ml_hg_rough" x="-10%" y="-10%" width="120%" height="120%">
                    <feTurbulence type="fractalNoise" baseFrequency="0.85" numOctaves="2" seed="3" />
                    <feDisplacementMap in="SourceGraphic" scale="1.6" />
                  </filter>
                </defs>
                {/* One closed bowtie path: top bar → right diag → bottom bar → left diag → close */}
                <path
                  d="M 18 20 L 102 20 L 60 70 L 102 120 L 18 120 L 60 70 Z"
                  stroke="#e5b649"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  fill="none"
                />
              </svg>
              <style>{`
                @keyframes ml_hg_spin {
                  0%   { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>

            {/* Title */}
            <h1
              className="text-[34px] sm:text-[38px] font-extrabold text-white leading-[1.05] tracking-tight"
              data-testid="deposit-pending-title"
            >
              Transaction in progress
            </h1>

            {/* Subtitle */}
            <p className="mt-4 text-[15px] sm:text-[16px] text-white/55 leading-[1.5] max-w-[360px]">
              Complete the payment in the new tab. You can track the progress in your transaction records.
            </p>

            {/* Card */}
            <div
              className="mt-10 w-full rounded-2xl text-left overflow-hidden"
              style={{
                background:
                  "linear-gradient(180deg, rgba(255,255,255,0.035) 0%, rgba(255,255,255,0.015) 100%)",
                border: "1px solid rgba(255,255,255,0.04)",
                boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset, 0 12px 24px -18px rgba(0,0,0,0.5)",
              }}
              data-testid="deposit-pending-card"
            >
              <div className="px-5 pt-5 pb-5">
                {/* Processing pill + Details (top row) */}
                <div className="flex items-start justify-between gap-3">
                  <span
                    className="inline-flex items-center text-[12px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-md"
                    style={{
                      color: "#f5c451",
                      background: "rgba(245,196,81,0.10)",
                    }}
                    data-testid="deposit-pending-status-pill"
                  >
                    Processing
                  </span>
                  <Link
                    to="/profile/transactions"
                    className="text-[14px] text-white/55 hover:text-white transition pt-0.5"
                    data-testid="deposit-pending-details-link"
                  >
                    Details
                  </Link>
                </div>

                {/* Main row: deposit info (left) + amount (right) */}
                <div className="mt-5 flex items-end justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-[26px] sm:text-[28px] font-extrabold text-white leading-none">
                      Deposit
                    </div>
                    <div
                      className="text-[15px] text-white/60 mt-1.5 truncate"
                      title={methodDisplay(deposit)}
                    >
                      {methodDisplay(deposit)}
                    </div>
                    {providerDisplay(deposit) && (
                      <div
                        className="text-[12px] text-white/40 mt-0.5 truncate"
                        title={providerDisplay(deposit)}
                        data-testid="deposit-pending-provider"
                      >
                        via {providerDisplay(deposit)}
                      </div>
                    )}
                    <div
                      className="mt-5 text-[13px] text-white/45 tabular-nums"
                      data-testid="deposit-pending-time"
                    >
                      {new Date(deposit.created_at)
                        .toLocaleString("en-GB", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                          hour: "2-digit",
                          minute: "2-digit",
                          second: "2-digit",
                          hour12: false,
                        })
                        .replace(",", "")}
                    </div>
                  </div>

                  <div
                    className="text-[30px] sm:text-[34px] font-extrabold tabular-nums leading-none shrink-0 pb-0.5"
                    style={{ color: "#21c46e" }}
                    data-testid="deposit-pending-amount"
                  >
                    +{fmtBDT(deposit.amount)}
                  </div>
                </div>
              </div>
            </div>

            {/* Gateway order number (subtle, below card) */}
            <button
              onClick={copyOrderNo}
              disabled={!gatewayOrderNo}
              className="mt-5 inline-flex items-center gap-2 text-[12px] text-white/40 hover:text-white/70 transition group disabled:hover:text-white/40 max-w-full"
              title={String(gatewayOrderNo || "")}
              data-testid="deposit-pending-order-no"
            >
              <span className="uppercase tracking-[0.16em] font-semibold whitespace-nowrap">
                Order Number
              </span>
              <span className="opacity-50">·</span>
              <span className="font-mono text-white/65 truncate max-w-[200px] sm:max-w-[280px]">
                {gatewayOrderNo || "—"}
              </span>
              {gatewayOrderNo ? (
                orderCopied ? (
                  <Check size={12} className="text-emerald-400 shrink-0" />
                ) : (
                  <Copy size={12} className="opacity-50 group-hover:opacity-100 shrink-0" />
                )
              ) : null}
            </button>
          </div>

          {/* Bottom CTA */}
          <div className="mt-6 pt-2">
            {checkoutUrl ? (
              <button
                onClick={() => window.open(checkoutUrl, "_blank", "noopener,noreferrer")}
                className="w-full h-[58px] rounded-xl text-white text-[17px] font-bold inline-flex items-center justify-center active:scale-[0.99] transition"
                style={{
                  background: "linear-gradient(180deg, #25cf78 0%, #1ea863 100%)",
                  boxShadow:
                    "0 1px 0 rgba(255,255,255,0.18) inset, 0 -1px 0 rgba(0,0,0,0.18) inset, 0 14px 34px -16px rgba(33,196,110,0.55)",
                }}
                data-testid="deposit-pending-go-to-payment"
              >
                Go to Payment Page
              </button>
            ) : (
              <Link
                to="/profile/transactions"
                className="w-full h-[58px] rounded-xl text-white text-[17px] font-bold inline-flex items-center justify-center active:scale-[0.99] transition"
                style={{
                  background: "linear-gradient(180deg, #25cf78 0%, #1ea863 100%)",
                  boxShadow:
                    "0 1px 0 rgba(255,255,255,0.18) inset, 0 -1px 0 rgba(0,0,0,0.18) inset, 0 14px 34px -16px rgba(33,196,110,0.55)",
                }}
                data-testid="deposit-pending-records"
              >
                View transaction records
              </Link>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ProfileShell title="Deposit status">
      <div
        className="max-w-xl mx-auto pb-24 sm:pb-10"
        data-testid="deposit-return-page"
      >
        {/* Back link */}
        <button
          onClick={() => navigate("/profile/wallet")}
          className="inline-flex items-center gap-1.5 text-xs sm:text-sm text-white/55 hover:text-white mb-4 transition"
          data-testid="deposit-return-back"
        >
          <ArrowLeft size={14} /> Back to wallet
        </button>

        {/* Re-open checkout (legacy banner) — only for non-pending edge cases */}
        {checkoutUrl && status === "pending" && !deposit && (
          <div
            className="mb-4 flex flex-col sm:flex-row items-start sm:items-center gap-3 p-3 sm:p-4 rounded-xl"
            style={{ background: "rgba(245,196,81,0.08)", border: "1px solid rgba(245,196,81,0.25)" }}
            data-testid="deposit-checkout-reopen-banner"
          >
            <div className="flex-1 text-xs sm:text-sm text-amber-100/85">
              <div className="font-semibold mb-0.5">Did the checkout tab close?</div>
              <div className="opacity-80">If you closed the payment window before finishing, you can re-open it.</div>
            </div>
            <button
              onClick={() => window.open(checkoutUrl, "_blank", "noopener,noreferrer")}
              className="text-xs sm:text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap"
              style={{ background: "#f5c451", color: "#1a1300" }}
              data-testid="deposit-checkout-reopen-btn"
            >
              Re-open checkout
            </button>
          </div>
        )}

        {/* Loading */}
        {loading && !deposit && (
          <div className="flex flex-col items-center justify-center py-20 text-white/50">
            <Loader2 className="animate-spin mb-3" size={28} style={{ color: "#21c46e" }} />
            <div className="text-sm">Loading deposit status…</div>
          </div>
        )}

        {/* Hard error */}
        {error && !deposit && (
          <div
            className="rounded-2xl border border-red-500/30 bg-red-500/10 text-red-200 p-5 text-sm"
            data-testid="deposit-return-error"
          >
            {error}
          </div>
        )}


        {/* Main card — non-pending states (completed / failed / rejected) */}
        {deposit && status !== "pending" && (
          <>
            {/* ====== Status Hero Card ====== */}
            <div
              className="relative rounded-2xl sm:rounded-3xl overflow-hidden"
              style={{
                background: "#0b1220",
                border: `1px solid ${meta.border}`,
                boxShadow: `0 20px 60px -30px ${meta.glow}`,
              }}
              data-testid={`deposit-return-status-${status}`}
            >
              {/* Top ribbon */}
              <div className="h-1.5" style={{ background: `linear-gradient(90deg, transparent 0%, ${meta.ribbon} 50%, transparent 100%)` }} />

              {/* Glow layers */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{ background: meta.bg }}
              />
              <div
                className="absolute -top-24 left-1/2 -translate-x-1/2 w-[320px] h-[320px] rounded-full pointer-events-none blur-3xl"
                style={{ background: `radial-gradient(circle, ${meta.glow} 0%, transparent 60%)` }}
              />

              {/* Success confetti */}
              {isDone && celebrated && <Confetti />}

              <div className="relative px-5 sm:px-8 pt-8 pb-7 text-center">
                {/* Icon badge with rings */}
                <div className="relative inline-flex items-center justify-center mb-4">
                  {/* animated rings */}
                  {status === "pending" && (
                    <>
                      <span
                        className="absolute inset-0 rounded-full animate-ping opacity-70"
                        style={{ background: `${meta.color}22`, animationDuration: "1.6s" }}
                      />
                      <span
                        className="absolute -inset-2 rounded-full animate-pulse"
                        style={{ background: `${meta.color}14` }}
                      />
                    </>
                  )}
                  {isDone && (
                    <span
                      className="absolute -inset-3 rounded-full animate-pulse"
                      style={{ background: `${meta.color}18`, animationDuration: "2.4s" }}
                    />
                  )}

                  <div
                    className="relative h-20 w-20 rounded-full flex items-center justify-center"
                    style={{
                      background: `linear-gradient(135deg, ${meta.color}2a, ${meta.color}10)`,
                      border: `1.5px solid ${meta.color}70`,
                      boxShadow: `0 10px 30px -10px ${meta.glow}, inset 0 1px 0 ${meta.color}55`,
                    }}
                  >
                    {status === "pending" ? (
                      <Loader2 size={42} className="animate-spin" style={{ color: meta.color }} />
                    ) : (
                      <Icon size={42} strokeWidth={2.3} style={{ color: meta.color }} />
                    )}
                  </div>
                </div>

                {/* Title — with celebration emojis for completed */}
                <div className="flex items-center justify-center gap-2 mb-2">
                  <h1
                    className="text-2xl sm:text-3xl font-black leading-tight tracking-tight"
                    style={{ color: meta.color }}
                  >
                    {meta.title}
                  </h1>
                  {isDone && (
                    <PartyPopper
                      size={26}
                      className="animate-bounce"
                      style={{ color: "#f5c451", animationDuration: "1.2s" }}
                    />
                  )}
                </div>

                {/* Subtitle */}
                <p
                  className="text-[13px] sm:text-sm leading-relaxed max-w-md mx-auto"
                  style={{ color: `${meta.softColor}` }}
                >
                  {meta.body}
                </p>

                {/* Pending tip when timed out */}
                {status === "pending" && attempts >= POLL_MAX_ATTEMPTS && (
                  <div className="mt-4 text-[11px] text-white/50 max-w-sm mx-auto">
                    Still processing? Tap Refresh — the gateway can take a few minutes during peak hours.
                  </div>
                )}

                {/* Amount spotlight (only on success) */}
                {isDone && (
                  <div
                    className="mt-5 inline-flex flex-col items-center py-3 px-7 rounded-2xl"
                    style={{
                      background:
                        "linear-gradient(180deg, rgba(33,196,110,0.14) 0%, rgba(33,196,110,0.04) 100%)",
                      border: "1px solid rgba(33,196,110,0.35)",
                    }}
                  >
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-emerald-400/90">
                      Credited
                    </div>
                    <div className="text-3xl sm:text-4xl font-black text-emerald-400 tabular-nums leading-none mt-0.5">
                      +৳{fmtBDT(deposit.amount)}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* ====== Details card ====== */}
            <div
              className="mt-4 rounded-2xl overflow-hidden"
              style={{
                background: "#0f1622",
                border: "1px solid rgba(255,255,255,0.06)",
              }}
              data-testid="deposit-return-details"
            >
              <div className="px-4 sm:px-5 py-3 border-b border-white/5 flex items-center gap-2">
                <ShieldCheck size={14} className="text-emerald-400" />
                <span className="text-[11px] uppercase tracking-[0.15em] font-bold text-white/55">
                  Transaction details
                </span>
              </div>
              <div className="divide-y divide-white/[0.05]">
                <DRow icon={Coins} label="Amount" value={`৳${fmtBDT(deposit.amount)}`} highlight={isDone} testid="row-amount" />
                <DRow
                  icon={Landmark}
                  label="Method"
                  value={methodDisplay(deposit)}
                  testid="row-method"
                />
                <DRow
                  icon={Wallet}
                  label="Gateway"
                  value={providerDisplay(deposit) || deposit.gateway_label || "—"}
                  testid="row-channel"
                />
                <DRow
                  icon={Hash}
                  label="Reference"
                  value={deposit.reference || deposit.id?.slice(0, 12)}
                  mono
                  testid="row-reference"
                />
                <DRow
                  icon={Hash}
                  label="Order Number"
                  value={gatewayOrderNo || "—"}
                  mono
                  testid="row-gateway-order-no"
                />
                <DRow
                  icon={Clock}
                  label="Time"
                  value={friendlyTime(deposit.created_at)}
                  testid="row-time"
                />
                <div className="flex items-center justify-between px-4 sm:px-5 py-3 text-sm" data-testid="row-status">
                  <div className="flex items-center gap-2 text-white/55">
                    <span
                      className="h-2 w-2 rounded-full"
                      style={{
                        background: meta.color,
                        boxShadow: `0 0 10px ${meta.color}`,
                      }}
                    />
                    <span className="text-xs font-semibold uppercase tracking-wider">Status</span>
                  </div>
                  <span
                    className="text-xs font-black uppercase tracking-wider px-2.5 py-1 rounded-full"
                    style={{
                      color: meta.color,
                      background: `${meta.color}18`,
                      border: `1px solid ${meta.color}40`,
                    }}
                  >
                    {status}
                  </span>
                </div>
              </div>
            </div>

            {/* ====== Action buttons ====== */}
            {isDone ? (
              /* SUCCESS layout — Home + Open Wallet (no refresh needed) */
              <div className="mt-5 grid grid-cols-2 gap-3" data-testid="deposit-return-actions-success">
                <Link
                  to="/"
                  className="h-12 rounded-xl bg-white/[0.06] border border-white/10 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:bg-white/10 active:scale-[0.98] transition"
                  data-testid="deposit-return-home"
                >
                  <Home size={15} /> Home
                </Link>
                <Link
                  to="/profile/wallet"
                  className="h-12 rounded-xl text-black text-sm font-black inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
                  style={{
                    background: "linear-gradient(180deg,#34de7f 0%,#21c46e 55%,#168f4f 100%)",
                    boxShadow: "0 10px 30px -10px rgba(33,196,110,0.6), inset 0 1px 0 rgba(255,255,255,0.35)",
                  }}
                  data-testid="deposit-return-wallet"
                >
                  <Wallet size={15} /> Open Wallet
                </Link>
              </div>
            ) : (
              /* PENDING / FAILED layout — Refresh + (retry | home) */
              <div className="mt-5 grid grid-cols-2 gap-3" data-testid="deposit-return-actions">
                <button
                  onClick={async () => {
                    await fetchOnce();
                    if (deposit?.provider === "cowpay" && deposit?.status === "pending") {
                      await requeryGateway();
                    }
                  }}
                  className="h-12 rounded-xl bg-white/[0.06] border border-white/10 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:bg-white/10 active:scale-[0.98] transition"
                  data-testid="deposit-return-refresh"
                >
                  <RefreshCw size={15} /> Refresh
                </button>
                {status === "pending" ? (
                  <Link
                    to="/"
                    className="h-12 rounded-xl bg-white/[0.06] border border-white/10 text-white text-sm font-bold inline-flex items-center justify-center gap-1.5 hover:bg-white/10 active:scale-[0.98] transition"
                    data-testid="deposit-return-home-pending"
                  >
                    <Home size={15} /> Home
                  </Link>
                ) : (
                  <Link
                    to="/profile/deposit"
                    className="h-12 rounded-xl text-black text-sm font-black inline-flex items-center justify-center gap-1.5 active:scale-[0.98] transition"
                    style={{
                      background: "linear-gradient(180deg,#34de7f 0%,#21c46e 55%,#168f4f 100%)",
                      boxShadow: "0 10px 30px -10px rgba(33,196,110,0.6), inset 0 1px 0 rgba(255,255,255,0.35)",
                    }}
                    data-testid="deposit-return-retry"
                  >
                    Try again
                  </Link>
                )}
              </div>
            )}

            {/* ====== Polling indicator ====== */}
            {status === "pending" && attempts < POLL_MAX_ATTEMPTS && (
              <div
                className="mt-3 text-[11px] text-white/40 text-center flex items-center justify-center gap-1.5"
                data-testid="deposit-return-polling"
              >
                <Loader2 size={10} className="animate-spin" style={{ color: "#21c46e" }} />
                Auto-checking status… ({attempts + 1}/{POLL_MAX_ATTEMPTS})
              </div>
            )}
          </>
        )}
      </div>
    </ProfileShell>
  );
}

/* ---------- Detail row ---------- */
function DRow({ icon: Icon, label, value, testid, mono, highlight }) {
  return (
    <div className="flex items-center justify-between px-4 sm:px-5 py-3 text-sm" data-testid={testid}>
      <div className="flex items-center gap-2 text-white/55 min-w-0">
        {Icon && <Icon size={13} className="shrink-0" strokeWidth={2} />}
        <span className="text-xs font-semibold uppercase tracking-wider truncate">{label}</span>
      </div>
      <div
        className={`font-bold text-white ml-3 ${mono ? "font-mono text-[12px] text-white/75" : "text-[14px]"} ${
          highlight ? "text-emerald-400" : ""
        } truncate max-w-[60%] text-right`}
        title={value}
      >
        {value}
      </div>
    </div>
  );
}

/* ---------- Confetti ---------- */
function Confetti() {
  const pieces = Array.from({ length: 28 });
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {pieces.map((_, i) => {
        const left = Math.random() * 100;
        const delay = Math.random() * 0.6;
        const duration = 1.4 + Math.random() * 1.2;
        const size = 5 + Math.random() * 6;
        const colors = ["#21c46e", "#f5c451", "#3b82f6", "#ec4899", "#ffffff"];
        const color = colors[i % colors.length];
        const rotate = Math.random() * 360;
        return (
          <span
            key={i}
            style={{
              position: "absolute",
              left: `${left}%`,
              top: "-12px",
              width: size,
              height: size * 1.6,
              background: color,
              borderRadius: 1,
              transform: `rotate(${rotate}deg)`,
              animation: `confetti-fall ${duration}s ${delay}s ease-in forwards`,
              opacity: 0,
            }}
          />
        );
      })}
      <style>{`
        @keyframes confetti-fall {
          0%   { transform: translateY(-20px) rotate(0deg); opacity: 1; }
          100% { transform: translateY(420px) rotate(720deg); opacity: 0; }
        }
      `}</style>
    </div>
  );
}
